import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, Sparkles, Zap, Crown, CreditCard, Loader2, Coins, Gift } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { cn } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

const subscriptionPlans = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    period: 'forever',
    monthlyCredits: 50,
    features: [
      '50 credits/month (auto-refresh)',
      'Standard video quality (5 credits)',
      '5 image generations/day',
      'Basic AI models',
      'Community support'
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 29,
    period: 'month',
    popular: true,
    monthlyCredits: 500,
    features: [
      '500 credits/month',
      'Credits roll over (max 1000)',
      'All video qualities (Standard + Sora 2)',
      '100 image generations/day',
      'Advanced AI models',
      'Priority support'
    ],
  },
  {
    id: 'premium',
    name: 'Premium',
    price: 79,
    period: 'month',
    monthlyCredits: 2000,
    features: [
      '2000 credits/month',
      'Unlimited credit rollover',
      'All video qualities (incl. Sora 2 Pro)',
      'Unlimited image generations',
      'All AI models',
      'API access',
      'Priority support'
    ],
  }
];

const creditPackages = [
  { id: 'starter', credits: 100, price: 5, bonus: 0 },
  { id: 'standard', credits: 300, price: 12, bonus: 30, popular: true },
  { id: 'pro', credits: 700, price: 25, bonus: 100 },
  { id: 'mega', credits: 2000, price: 60, bonus: 400 },
];

const SubscriptionPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [currentPlan, setCurrentPlan] = useState('free');
  const [creditBalance, setCreditBalance] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  useEffect(() => {
    // Check for payment success from URL
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
    
    // Get current subscription and credits
    fetchSubscriptionStatus();
  }, [searchParams, user]);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await api.get('/api/subscription/status');
      setCurrentPlan(response.data.plan_id || 'free');
      setCreditBalance(response.data.credits?.balance || 0);
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await api.get(`/api/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setPaymentSuccess(true);
        fetchSubscriptionStatus();
      }
    } catch (error) {
      console.error('Failed to check payment status:', error);
    }
  };

  const planIcons = {
    free: Sparkles,
    pro: Zap,
    premium: Crown
  };

  const handleSelectPlan = (plan) => {
    if (plan.id === currentPlan || plan.id === 'free') return;
    setSelectedPlan(plan);
    setShowPaymentModal(true);
  };

  const handlePayment = async () => {
    if (!selectedPlan) return;
    
    setIsProcessing(true);
    try {
      const response = await api.post('/api/payments/checkout', {
        package_id: selectedPlan.id,
        origin_url: window.location.origin,
        payment_method: paymentMethod
      });
      
      // Redirect to Stripe/PayPal checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert(error.response?.data?.detail || 'Payment failed. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-full bg-zinc-950 p-4 sm:p-6 pb-24">
      <div className="max-w-5xl mx-auto">
        {/* Success Message */}
        {paymentSuccess && (
          <div className="mb-6 p-4 bg-emerald-500/20 border border-emerald-500/30 rounded-lg text-center">
            <Check className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
            <h3 className="text-lg font-semibold text-emerald-400">Payment Successful!</h3>
            <p className="text-emerald-300">Your subscription has been activated.</p>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-white mb-3">Choose Your Plan</h1>
          <p className="text-zinc-400 max-w-xl mx-auto">
            Unlock the full potential of AI with our subscription plans. 
            Get more generations, faster responses, and exclusive features.
          </p>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {subscriptionPlans.map((plan) => {
            const Icon = planIcons[plan.id];
            const isCurrentPlan = plan.id === currentPlan;
            
            return (
              <Card
                key={plan.id}
                className={cn(
                  "relative bg-zinc-900 border-zinc-800 transition-all duration-300 hover:border-zinc-700",
                  plan.popular && "border-emerald-500/50 shadow-lg shadow-emerald-500/10",
                  isCurrentPlan && "ring-2 ring-emerald-500"
                )}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-gradient-to-r from-emerald-500 to-cyan-500 text-white border-none">
                      Most Popular
                    </Badge>
                  </div>
                )}
                
                <CardHeader className="text-center pb-4">
                  <div className={cn(
                    "w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center",
                    plan.id === 'free' && "bg-zinc-800",
                    plan.id === 'pro' && "bg-gradient-to-br from-emerald-500 to-cyan-500",
                    plan.id === 'premium' && "bg-gradient-to-br from-amber-500 to-orange-500"
                  )}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <CardTitle className="text-xl text-white">{plan.name}</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-white">${plan.price}</span>
                    {plan.price > 0 && (
                      <span className="text-zinc-400">/{plan.period}</span>
                    )}
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  <ul className="space-y-3">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                        <Check className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button
                    onClick={() => handleSelectPlan(plan)}
                    disabled={isCurrentPlan || plan.id === 'free'}
                    className={cn(
                      "w-full mt-6",
                      plan.popular 
                        ? "bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-white"
                        : isCurrentPlan
                          ? "bg-zinc-800 text-zinc-400 cursor-not-allowed"
                          : "bg-zinc-800 hover:bg-zinc-700 text-white"
                    )}
                  >
                    {isCurrentPlan ? 'Current Plan' : plan.price === 0 ? 'Free Forever' : 'Subscribe'}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Credit Balance Banner */}
        <div className="mt-8 p-4 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-xl">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                <Coins className="h-5 w-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-white font-medium">Your Credit Balance</p>
                <p className="text-2xl font-bold text-yellow-400">{creditBalance} credits</p>
              </div>
            </div>
            <Button
              onClick={() => setShowCreditModal(true)}
              className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 text-white"
            >
              <Coins className="h-4 w-4 mr-2" />
              Buy Credits
            </Button>
          </div>
        </div>

        {/* Credit Packages */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-white text-center mb-2">Buy Credit Packs</h2>
          <p className="text-zinc-400 text-center mb-8">Need more credits? Get instant top-ups</p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {creditPackages.map((pkg) => (
              <Card
                key={pkg.id}
                className={cn(
                  "relative bg-zinc-900 border-zinc-800 transition-all duration-300 hover:border-yellow-500/50 cursor-pointer",
                  pkg.popular && "border-yellow-500/50 shadow-lg shadow-yellow-500/10"
                )}
                onClick={() => {
                  setSelectedPackage(pkg);
                  setShowCreditModal(true);
                }}
              >
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white border-none text-xs">
                      Best Value
                    </Badge>
                  </div>
                )}
                <CardContent className="p-4 text-center">
                  <div className="flex items-center justify-center gap-1 mb-2">
                    <Coins className="h-5 w-5 text-yellow-400" />
                    <span className="text-2xl font-bold text-white">{pkg.credits}</span>
                  </div>
                  {pkg.bonus > 0 && (
                    <div className="flex items-center justify-center gap-1 text-xs text-green-400 mb-2">
                      <Gift className="h-3 w-3" />
                      +{pkg.bonus} bonus
                    </div>
                  )}
                  <div className="text-lg font-semibold text-yellow-400">${pkg.price}</div>
                  <div className="text-xs text-zinc-500 mt-1">
                    ${(pkg.price / (pkg.credits + pkg.bonus)).toFixed(3)}/credit
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Credit Cost Info */}
        <div className="mt-8 p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl">
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <Zap className="h-5 w-5 text-cyan-400" />
            Credit Costs
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="p-3 bg-zinc-800/50 rounded-lg">
              <p className="text-zinc-400">Standard Video</p>
              <p className="text-white font-medium">5 credits</p>
            </div>
            <div className="p-3 bg-zinc-800/50 rounded-lg">
              <p className="text-zinc-400">Pro Video (Sora 2)</p>
              <p className="text-white font-medium">15 credits</p>
            </div>
            <div className="p-3 bg-zinc-800/50 rounded-lg">
              <p className="text-zinc-400">Premium Video</p>
              <p className="text-white font-medium">50 credits</p>
            </div>
            <div className="p-3 bg-zinc-800/50 rounded-lg">
              <p className="text-zinc-400">Image Generation</p>
              <p className="text-white font-medium">1 credit</p>
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="mt-8 text-center">
          <p className="text-zinc-400 text-sm">
            Credits never expire. Subscription credits refresh monthly.
            <br />
            Secure payments powered by Stripe and PayPal.
          </p>
        </div>
      </div>

      {/* Credit Purchase Modal */}
      <Dialog open={showCreditModal} onOpenChange={setShowCreditModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Buy Credits</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Select a credit package
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 mt-4">
            {creditPackages.map((pkg) => (
              <button
                key={pkg.id}
                onClick={() => setSelectedPackage(pkg)}
                className={cn(
                  "w-full p-4 rounded-lg border-2 transition-all flex items-center justify-between",
                  selectedPackage?.id === pkg.id
                    ? "border-yellow-500 bg-yellow-500/10"
                    : "border-zinc-700 hover:border-zinc-600"
                )}
              >
                <div className="flex items-center gap-3">
                  <Coins className="h-5 w-5 text-yellow-400" />
                  <div className="text-left">
                    <p className="text-white font-medium">{pkg.credits} credits</p>
                    {pkg.bonus > 0 && (
                      <p className="text-xs text-green-400">+{pkg.bonus} bonus credits</p>
                    )}
                  </div>
                </div>
                <span className="text-xl font-bold text-yellow-400">${pkg.price}</span>
              </button>
            ))}
          </div>
          
          <Button
            onClick={async () => {
              if (!selectedPackage) return;
              setIsProcessing(true);
              try {
                const response = await api.post('/api/payments/buy-credits', { 
                  package_id: selectedPackage.id,
                  origin_url: window.location.origin
                });
                // Redirect to Stripe checkout
                if (response.data.url) {
                  window.location.href = response.data.url;
                }
              } catch (error) {
                console.error('Credit purchase error:', error);
                alert(error.response?.data?.detail || 'Failed to start checkout. Please try again.');
              } finally {
                setIsProcessing(false);
              }
            }}
            disabled={!selectedPackage || isProcessing}
            className="w-full mt-4 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 h-12"
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CreditCard className="h-4 w-4 mr-2" />
                Buy {selectedPackage ? selectedPackage.credits + (selectedPackage.bonus || 0) : 0} Credits
              </>
            )}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Complete Your Subscription</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Subscribe to {selectedPlan?.name} for ${selectedPlan?.price}/month
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-zinc-800 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-zinc-300">{selectedPlan?.name} Plan</span>
                <span className="text-white font-semibold">${selectedPlan?.price}/mo</span>
              </div>
            </div>
            
            {/* Payment Method Selection */}
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Payment Method</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setPaymentMethod('stripe')}
                  className={cn(
                    "p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2",
                    paymentMethod === 'stripe' 
                      ? "border-emerald-500 bg-emerald-500/10" 
                      : "border-zinc-700 hover:border-zinc-600"
                  )}
                >
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z" className="text-indigo-400"/>
                  </svg>
                  <span className="text-white">Stripe</span>
                </button>
                
                <button
                  onClick={() => setPaymentMethod('paypal')}
                  className={cn(
                    "p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2",
                    paymentMethod === 'paypal' 
                      ? "border-emerald-500 bg-emerald-500/10" 
                      : "border-zinc-700 hover:border-zinc-600"
                  )}
                >
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z" className="text-blue-400"/>
                  </svg>
                  <span className="text-white">PayPal</span>
                </button>
              </div>
            </div>
            
            <Button
              onClick={handlePayment}
              disabled={isProcessing}
              className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 h-12"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Redirecting to {paymentMethod === 'stripe' ? 'Stripe' : 'PayPal'}...
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Pay ${selectedPlan?.price} with {paymentMethod === 'stripe' ? 'Stripe' : 'PayPal'}
                </>
              )}
            </Button>
            
            <p className="text-xs text-zinc-500 text-center">
              Secure payment. Cancel anytime.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubscriptionPage;
